import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { knowledgeService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, FileText, HelpCircle, FolderArchive } from "lucide-react";
import { STATUS_COLOR, VISIBILITY_COLOR } from "./constants";

const TYPE_META = {
  article: { icon: FileText, label: "Article", color: "text-sky-500" },
  faq: { icon: HelpCircle, label: "FAQ", color: "text-emerald-500" },
  document: { icon: FolderArchive, label: "Document", color: "text-violet-500" },
};
const TYPES = ["article", "faq", "document"];

export default function KnowledgeSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [types, setTypes] = useState(["article", "faq", "document"]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = useCallback(async () => {
    setLoading(true); setSearched(true);
    try {
      const d = await knowledgeService.search({ q, types: types.join(","), limit: 30 });
      setResults(d.results || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [q, types]);

  const toggleType = (t) => setTypes((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);

  const openResult = (r) => {
    if (r.type === "article") {
      knowledgeService.recordUsage({ knowledgeType: "article", knowledgeId: r.id, action: "view" }).catch(() => {});
      navigate(`/admin/knowledge/articles/${r.id}`);
    } else if (r.type === "faq") {
      navigate("/admin/knowledge/faqs");
    } else {
      navigate("/admin/knowledge/documents");
    }
  };

  return (
    <div data-testid="knowledge-search-page">
      <PageHeader title="Knowledge Search" subtitle="Search across articles, FAQs and documents — results respect your visibility permissions" />
      <div className="bg-card rounded-xl border border-border p-4 shadow-sm mb-6">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()}
              placeholder="Search knowledge…" className="ciq-input pl-9" data-testid="kb-search-input" />
          </div>
          <button onClick={run} disabled={loading} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="kb-search-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
          </button>
        </div>
        <div className="flex items-center gap-2 mt-3">
          {TYPES.map((t) => {
            const M = TYPE_META[t]; const on = types.includes(t);
            return (
              <button key={t} onClick={() => toggleType(t)} data-testid={`kb-search-type-${t}`}
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-primary/10 border-primary text-primary" : "border-border text-muted-foreground hover:bg-muted"}`}>
                <M.icon className="h-3.5 w-3.5" /> {M.label}s
              </button>
            );
          })}
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : searched && (
        <div data-testid="kb-search-results">
          <p className="text-sm text-muted-foreground mb-3">{results?.length || 0} result(s){q ? ` for "${q}"` : ""}</p>
          {(!results || results.length === 0) ? (
            <p className="text-sm text-muted-foreground py-10 text-center">No matching knowledge found.</p>
          ) : (
            <div className="space-y-2">
              {results.map((r) => {
                const M = TYPE_META[r.type] || TYPE_META.article;
                return (
                  <button key={`${r.type}-${r.id}`} onClick={() => openResult(r)}
                    className="w-full text-left bg-card rounded-xl border border-border p-4 hover:border-primary/50 transition-colors" data-testid={`kb-result-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <M.icon className={`h-5 w-5 mt-0.5 shrink-0 ${M.color}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-foreground">{r.title}</span>
                          <span className="text-xs text-muted-foreground">{M.label}</span>
                          {r.status && <Badge color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Badge>}
                          {r.visibility && <Badge color={VISIBILITY_COLOR[r.visibility] || "slate"}>{r.visibility}</Badge>}
                        </div>
                        {r.snippet && <p className="text-sm text-muted-foreground line-clamp-2">{r.snippet}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
